import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Send } from 'lucide-react';
import { toast } from 'sonner';
import { getApiUrl } from '../lib/api';
import FileUpload from './FileUpload';

const WORK_TYPES = [
  'Ravalement de façade',
  'Isolation thermique extérieure (ITE)',
  'Rénovation tous corps d’état',
  'Étanchéité toiture / terrasse',
  'Couverture / toiture',
  'Rénovation énergétique',
  'Rénovation intérieure',
  'Autre',
];
const BUILDING_TYPES = [
  'Appartement',
  'Maison individuelle',
  'Immeuble / copropriété',
  'Local professionnel',
  'Autre',
];
const BUDGETS = [
  '< 15 000 €',
  '15 000 € – 50 000 €',
  '50 000 € – 150 000 €',
  '> 150 000 €',
  'Je ne sais pas encore',
];
const CLIENT_TYPES = ['Particulier', 'Copropriété / Syndic', 'Professionnel'];
const ROLES = [
  'Architecte',
  'Syndic',
  "Maître d'œuvre",
  'Agent immobilier',
  "Apporteur d'affaires",
  'Autre',
];

const emptyForm = (isPartner, defaultWorkType = '', defaultCity = '') => ({
  name: '',
  email: '',
  phone: '',
  city: defaultCity,
  projectType: defaultWorkType,
  clientType: isPartner ? 'Professionnel' : '',
  budget: '',
  company: '',
  role: '',
  buildingType: '',
  surface: '',
  message: '',
});

export const QuoteForm = ({ variant = 'standard', defaultWorkType = '', defaultCity = '' }) => {
  const isPartner = variant === 'partner';
  const [form, setForm] = useState(emptyForm(isPartner, defaultWorkType, defaultCity));
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const change = (e) => set(e.target.name, e.target.value);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.projectType) {
      toast.error('Merci de sélectionner le type de travaux.');
      return;
    }
    setSubmitting(true);
    try {
      const extra = [];
      if (!isPartner && form.buildingType) extra.push(`Type de bâtiment : ${form.buildingType}`);
      if (!isPartner && form.surface) extra.push(`Surface approximative : ${form.surface} m²`);
      const fullMessage = extra.length ? `${extra.join('\n')}\n\n${form.message}` : form.message;
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        city: form.city,
        project_type: form.projectType,
        client_type: isPartner ? 'Professionnel' : form.clientType || 'Particulier',
        budget: form.budget || null,
        company: form.company || null,
        role: isPartner ? form.role || null : null,
        source: isPartner ? 'partenaires' : 'contact',
        message: fullMessage,
        file_ids: files.map((f) => f.id),
      };
      const res = await fetch(`${getApiUrl()}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message || 'Demande envoyée. Nous revenons vers vous rapidement.');
        setForm(emptyForm(isPartner, defaultWorkType, defaultCity));
        setFiles([]);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } catch {
      toast.error('Erreur de connexion. Vérifiez votre réseau et réessayez.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid={isPartner ? 'partner-form' : 'quote-form'}>
      {isPartner && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Société / cabinet</Label>
            <Input
              name="company"
              value={form.company}
              onChange={change}
              placeholder="Nom de votre structure"
              className="border-slate-300"
              data-testid="form-company"
            />
          </div>
          <div className="space-y-2">
            <Label>Vous êtes *</Label>
            <Select value={form.role} onValueChange={(v) => set('role', v)}>
              <SelectTrigger className="border-slate-300" data-testid="form-role">
                <SelectValue placeholder="Votre profession" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Nom complet *</Label>
          <Input name="name" value={form.name} onChange={change} required placeholder="Jean Dupont" className="border-slate-300" data-testid="form-name" />
        </div>
        <div className="space-y-2">
          <Label>Email *</Label>
          <Input name="email" type="email" value={form.email} onChange={change} required placeholder="jean@email.com" className="border-slate-300" data-testid="form-email" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Téléphone *</Label>
          <Input name="phone" type="tel" value={form.phone} onChange={change} required placeholder="06 12 34 56 78" className="border-slate-300" data-testid="form-phone" />
        </div>
        <div className="space-y-2">
          <Label>Ville / secteur *</Label>
          <Input name="city" value={form.city} onChange={change} required placeholder="Paris 8e" className="border-slate-300" data-testid="form-city" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {!isPartner && (
          <div className="space-y-2">
            <Label>Type de projet</Label>
            <Select value={form.clientType} onValueChange={(v) => set('clientType', v)}>
              <SelectTrigger className="border-slate-300" data-testid="form-client-type">
                <SelectValue placeholder="Particulier, copropriété…" />
              </SelectTrigger>
              <SelectContent>
                {CLIENT_TYPES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-2">
          <Label>Type de travaux *</Label>
          <Select value={form.projectType} onValueChange={(v) => set('projectType', v)}>
            <SelectTrigger className="border-slate-300" data-testid="form-work-type">
              <SelectValue placeholder="Sélectionnez" />
            </SelectTrigger>
            <SelectContent>
              {WORK_TYPES.map((w) => (
                <SelectItem key={w} value={w}>{w}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Budget estimé</Label>
          <Select value={form.budget} onValueChange={(v) => set('budget', v)}>
            <SelectTrigger className="border-slate-300" data-testid="form-budget">
              <SelectValue placeholder="Sélectionnez" />
            </SelectTrigger>
            <SelectContent>
              {BUDGETS.map((b) => (
                <SelectItem key={b} value={b}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {!isPartner && (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Type de bâtiment</Label>
            <Select value={form.buildingType} onValueChange={(v) => set('buildingType', v)}>
              <SelectTrigger className="border-slate-300" data-testid="form-building-type">
                <SelectValue placeholder="Appartement, maison…" />
              </SelectTrigger>
              <SelectContent>
                {BUILDING_TYPES.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Surface approximative (m²)</Label>
            <Input
              name="surface"
              type="number"
              min="0"
              value={form.surface}
              onChange={change}
              placeholder="Ex. 80"
              className="border-slate-300"
              data-testid="form-surface"
            />
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>{isPartner ? "Détails de l'affaire *" : 'Détails du projet *'}</Label>
        <Textarea
          name="message"
          value={form.message}
          onChange={change}
          rows={5}
          required
          placeholder={
            isPartner
              ? 'Type de bien, localisation, nature des travaux, échéance de consultation…'
              : 'Type de bien, surface approximative, nature des travaux, délais souhaités…'
          }
          className="border-slate-300 resize-none"
          data-testid="form-message"
        />
      </div>

      <div className="space-y-2">
        <Label>Pièces jointes</Label>
        <FileUpload files={files} onChange={setFiles} testId="form-files" />
      </div>

      <Button
        type="submit"
        disabled={submitting}
        className="w-full bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white py-6 text-lg transition-all duration-300 hover:scale-[1.02] shadow-lg"
        data-testid="form-submit"
      >
        {submitting ? (
          'Envoi en cours…'
        ) : (
          <>
            <Send className="mr-2 h-5 w-5" />
            {isPartner ? 'Transmettre mon affaire' : 'Recevoir mon devis gratuit'}
          </>
        )}
      </Button>

      <p className="text-sm text-slate-500 text-center">
        En soumettant ce formulaire, vous acceptez d'être recontacté par VIREO concernant votre demande.
      </p>
    </form>
  );
};

export default QuoteForm;
